// orderDataTransformer.js - FIXED to extract selSubPrdVal + combination

/**
 * Transform order API data to the format expected by PackageDetailsPopup
 * This handles the FLAT props structure where data is at props.productName, props.sku, etc.
 */
export const transformOrderDataForPackageDetails = (props) => {
  
  if (!props) {
    console.error('❌ TRANSFORMER: No props provided');
    return null;
  }


  // ✅ YOUR DATA IS FLAT! Props structure:
  // props.orderId, props.productName, props.sku, props.image, props.invoices, etc.

  // Get product image
  const getProductImage = () => {
    if (props.image) {
      return [{
        image: props.image,
        baseImage: true
      }];
    }
    
    // Try from nested order structure (fallback)
    if (props.order?.products?.[0]?.productId?.images) {
      return props.order.products[0].productId.images;
    }
    
    return [];
  };

  // ✅ FIXED: Get product attributes/configuration
  const getProductAttributes = () => {

    // ✅ METHOD 1: Extract from selSubPrdVal + combination (FLAT PROPS - YOUR DATA!)
    if (props.selSubPrdVal && Array.isArray(props.selSubPrdVal) && props.selSubPrdVal.length > 0) {
      
      const attributeNames = props.combination || [];
      const attributeValues = props.selSubPrdVal;
      
      const attributes = attributeValues.map((value, idx) => ({
        name: attributeNames[idx] || `Attribute ${idx + 1}`,
        value: value
      }));
      return attributes;
    }
    
    // METHOD 2: Extract from order.products[0] (NESTED API RESPONSE)
    if (props.order?.products?.[0]) {
      const product = props.order.products[0];
      
      // Check selSubPrd.value
      if (product.selSubPrd && product.selSubPrd.value && Array.isArray(product.selSubPrd.value) && product.selSubPrd.value.length > 0) {
        
        // Get attribute names from combination[0].value
        let attributeNames = [];
        if (product.productId?.combination && 
            Array.isArray(product.productId.combination) && 
            product.productId.combination.length > 0 &&
            product.productId.combination[0]?.value) {
          attributeNames = product.productId.combination[0].value;
          
        }
        
        const values = product.selSubPrd.value;
        const attributes = values.map((value, idx) => ({
          name: attributeNames[idx] || `Attribute ${idx + 1}`,
          value: value
        }));
        
        
        return attributes;
      }
    }
    
    // METHOD 3: Check props.attributes (pre-formatted)
    if (props.attributes && Array.isArray(props.attributes) && props.attributes.length > 0) {
      
      return props.attributes;
    }
    
    // METHOD 4: Check props.configuration
    if (props.configuration && Array.isArray(props.configuration) && props.configuration.length > 0) {
      
      return props.configuration;
    }
    
    // METHOD 5: Check props.order.products[0].productId.attributes
    if (props.order?.products?.[0]?.productId?.attributes && 
        Array.isArray(props.order.products[0].productId.attributes) && 
        props.order.products[0].productId.attributes.length > 0) {
      
      return props.order.products[0].productId.attributes;
    }
    
    // METHOD 6: Try to construct from individual flat props (e.g., props.size, props.color)
    const constructedAttrs = [];
    if (props.size) {
      constructedAttrs.push({ name: 'Size', value: props.size });
    }
    if (props.color) {
      constructedAttrs.push({ name: 'Color', value: props.color });
    }
    
    if (constructedAttrs.length > 0) {
      
      return constructedAttrs;
    }
    
    console.warn('❌ No configuration data found in any location');
    return [];
  };

  // Get product URL
  const getProductUrl = () => {
    // Method 1: Check props.url
    if (props.url) {
      return props.url;
    }
    
    // Method 2: Check props.productUrl
    if (props.productUrl) {
      return props.productUrl;
    }
    
    // Method 3: Check props.order.products[0].productId.url
    if (props.order?.products?.[0]?.productId?.url) {
      return props.order.products[0].productId.url;
    }
    
    // Method 4: Check props.order.products[0].productId.urlKey
    if (props.order?.products?.[0]?.productId?.urlKey) {
      return props.order.products[0].productId.urlKey;
    }
    
    return null;
  };

  // Get tracks (invoices)
  const getTracks = () => {
    // ✅ Method 1: Check props.invoices (MOST COMMON for your data)
    if (props.invoices && Array.isArray(props.invoices) && props.invoices.length > 0) {
      return props.invoices;
    }
    
    // Method 2: Check props.order.products[0].tracks
    if (props.order?.products?.[0]?.tracks && props.order.products[0].tracks.length > 0) {
      return props.order.products[0].tracks;
    }
    
    // Method 3: Check props.tracks
    if (props.tracks && Array.isArray(props.tracks) && props.tracks.length > 0) {
      return props.tracks;
    }

    if (props.order?.products?.[0]?.revokes && 
        props.order.products[0].revokes.length > 0) {
       return props.order.products[0].revokes;
    }

    return [];
  };

  const tracks = getTracks();

  // Get shipping address
  const getShippingAddress = () => {
    const sources = [
      props.order?.shippingAddress,
      props.shippingAddress,
      props.order?.shipping_address,
      props.shipping_address
    ];
    
    for (const addr of sources) {
      if (addr && typeof addr === 'object') {
        return addr;
      }
    }
    
    console.warn('⚠️ TRANSFORMER: No shipping address found');
    return null;
  };

  // ✅ CRITICAL FIX: Create products array with data from FLAT props
  const products = [];
  
  const attributes = getProductAttributes();
  
  // Build product object from flat props
  const productData = {
    productId: {
      productName: props.productName || 'Unknown Product',
      SKU: props.sku || props.SKU || 'N/A',
      price: props.price || 0,
      taxClass: props.taxClass || 'N/A',
      images: getProductImage(),
      'Specification-Weight': (
      props.order?.products?.[0]?.productId?.['Specification-Weight'] ||
      props?.['Specification-Weight'] ||
      null
    ),
      attributes: attributes, // ✅ NOW PROPERLY POPULATED
      url: getProductUrl()
    },
    quantity: props.quantity || 1,
    tracks: tracks.map((track, index) => ({
      _id: track._id || `track_${index}`,
      productName: track.productName || props.productName || `Package ${index + 1}`,
      docketNo: track.docketNo || '',
      mDocketNo: track.mDocketNo || '',
      noOfPkgs: track.noOfPkgs || 1,
      shippingBy: track.shippingBy || '',
      codAmt: track.codAmt || 0,
      weight: track.weight || 0,
      vWeight: track.vWeight || 0,
      pkgWeight: track.pkgWeight || track.weight || 0,
      // Actual dimensions (PRIORITY)
      actHeight: track.actHeight || 0,
      actWidth: track.actWidth || 0,
      actLength: track.actLength || 0,
      actDeadWeight: track.actDeadWeight || 0,
      // Package dimensions (FALLBACK)
      packageHeight: track.packageHeight || 0,
      packageWidth: track.packageWidth || 0,
      packageLength: track.packageLength || 0,
      // Other details
      declared_price: track.declared_price || 0,
      docketStatus: track.docketStatus || '',
      rStatus: track.rStatus || '',
      invoice: track.invoice || '',
      edd: track.edd || null,
      transitDetail: track.transitDetail || [],
      created_at: track.created_at || null,
      hasReturn: track.hasReturn || false,
      redispatch: track.redispatch || false
    }))
  };

  products.push(productData);

  // Build final transformed data
  const transformedData = {
    _id: props.orderMainId || props._id || 'N/A',
    orderId: props.orderId || 'N/A',
    orderMainId: props.orderMainId || props._id,
    orderPlaceDate: props.order?.orderPlaceDate || props.order?.created_at || props.date || null,
    statusUpdateDate: props.updatedAt || props.date || null,
    shippingAddress: getShippingAddress(),
    products: products
  };


  return transformedData;
};

/**
 * Get order data directly from props for immediate use
 */
export const getPackageDetailsData = (props) => {
  return transformOrderDataForPackageDetails(props);
};